import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchCombobox } from "@/components/SearchCombobox";
import { Plus, X, TriangleAlert } from "lucide-react";

// value items: { part_id, quantity } for registered, { name, unit, quantity } for unregistered
export const PartsPicker = ({ parts, value, onChange }) => {
  const [sel, setSel] = useState(null); // { part_id } | { name }
  const [qty, setQty] = useState(1);
  const byId = Object.fromEntries(parts.map((p) => [p.id, p]));
  const keyOf = (v) => v.part_id || `new:${v.name.toLowerCase()}`;

  const add = () => {
    if (!sel || Number(qty) <= 0) return;
    const item = sel.part_id ? { part_id: sel.part_id, quantity: Number(qty) } : { name: sel.name, unit: "un", quantity: Number(qty) };
    onChange([...value.filter((v) => keyOf(v) !== keyOf(item)), item]);
    setSel(null);
    setQty(1);
  };

  const items = parts.map((p) => ({
    value: p.id,
    label: p.name,
    search: `${p.code} ${p.name} ${p.location || ""}`,
    disabled: (p.quantity || 0) <= 0,
    render: <span><span className="font-mono-tag text-orange-600 mr-2">{p.code}</span>{p.name} <span className="text-xs text-muted-foreground">— saldo {p.quantity} {p.unit}</span></span>,
  }));
  const selLabel = sel?.part_id ? `${byId[sel.part_id]?.code} — ${byId[sel.part_id]?.name}` : sel ? `${sel.name} (não cadastrada)` : "";

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <SearchCombobox
            items={items}
            value={sel?.part_id || (sel ? "new" : "")}
            selectedLabel={selLabel}
            onChange={(id) => setSel({ part_id: id })}
            onCreate={(name) => setSel({ name })}
            createLabel={(q) => `Usar "${q}" (não cadastrada — avisar almoxarife)`}
            placeholder="Buscar peça por código ou nome"
            emptyText="Nenhuma peça cadastrada"
            testId="parts-picker-select"
          />
        </div>
        <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="rounded-sm w-24" data-testid="parts-picker-qty" />
        <Button type="button" variant="outline" onClick={add} className="rounded-sm" data-testid="parts-picker-add"><Plus className="w-4 h-4" /></Button>
      </div>
      {value.map((v) => {
        const p = v.part_id ? byId[v.part_id] : null;
        const over = p && v.quantity > (p.quantity || 0);
        const k = keyOf(v);
        return (
          <div key={k} className={`flex items-center gap-2 text-sm border rounded-sm px-2 py-1.5 ${over ? "border-red-500 text-red-600" : ""} ${!v.part_id ? "border-amber-500/50 bg-amber-500/5" : ""}`} data-testid={`parts-picker-item-${v.part_id || "new"}`}>
            {p ? <span className="font-mono-tag text-orange-600">{p.code}</span> : <TriangleAlert className="w-3.5 h-3.5 text-amber-600" />}
            <span className="flex-1">{p ? p.name : v.name}{!v.part_id && <span className="text-xs text-amber-700 dark:text-amber-400 ml-1">(não cadastrada — almoxarife será avisado)</span>}</span>
            <span className="font-semibold">{v.quantity} {p ? p.unit : v.unit}</span>
            {over && <span className="text-xs">saldo insuficiente</span>}
            <button type="button" onClick={() => onChange(value.filter((x) => keyOf(x) !== k))} className="text-muted-foreground hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
};
